import type { ParsedFactoid } from "@/components/HeroStats/types";

export type DegreePageDesign51 = {
  uuid: string;
  showUSNewLogo: boolean | null;
  rankingText: string | null;
  rankingDescription?: string | null;
  programDates: string;
  programDuration: string | null;
  colorBarHexValue: string;
  /** When false, skip the Free application / Apply for free today hero factoid. Defaults to true. */
  showFreeApplication?: boolean;
  /** Renders in place of the free-application factoid. */
  replacementFactoid?: ReplacementFactoid;
};

export type ReplacementFactoid = {
  headingLabel: keyof Degree51Labels;
  descriptionLabel: keyof Degree51Labels;
};

export const MARYVILLE_PARTNER_UUID = "8e585adc-f870-4e8f-abe8-e67da192d49c";

export const MARYVILLE_REPLACEMENT_FACTOID: ReplacementFactoid = {
  headingLabel: "hassleFreeAdmissions",
  descriptionLabel: "noEntranceExamsAndCreditTransfer",
};

export function buildReplacementFactoid(
  replacement: ReplacementFactoid,
  labels: Degree51Labels,
  sortOrder: number,
): ParsedFactoid {
  return {
    heading: labels[replacement.headingLabel],
    description: labels[replacement.descriptionLabel],
    sortOrder,
  };
}

export type Degree51Labels = {
  rankedByUsNews: string;
  online100: string;
  programType: string;
  flexibleDates: string;
  programDuration: string;
  learnOnYourSchedule: string;
  freeApplication: string;
  applyForFreeToday: string;
  hassleFreeAdmissions: string;
  noEntranceExamsAndCreditTransfer: string;
};

const degreePageDesign51: DegreePageDesign51[] = [
  {
    // DegreePageDesign51LondonAll - University of London
    // /bachelors/universityoflondon
    uuid: "5427ee88-6dba-46ff-8ed8-87b71e3234da",
    showUSNewLogo: null,
    rankingText: null,
    programDates: "August, November, February, May",
    programDuration: "Self-paced",
    colorBarHexValue: "#E63635",
  },
  {
    // DegreePageDesign51MaryVille - University of Maryville
    // /bachelors/maryville OR /masters/maryville
    uuid: MARYVILLE_PARTNER_UUID,
    showUSNewLogo: null,
    rankingText: null,
    programDates: "Fall, Spring, Summer",
    programDuration: null,
    colorBarHexValue: "#C91235",
    showFreeApplication: false,
    replacementFactoid: MARYVILLE_REPLACEMENT_FACTOID,
  },
  {
    // DegreePageDesign51NYUCounseling - NYU Steinhardt Online Mental Health Counseling Master's
    // /masters/counseling/online-masters-in-mental-health-counseling/nyu-master-of-arts-in-counseling-for-mental-health-and-wellness
    uuid: "32343235-d926-418d-b803-a8fa04ba6888",
    showUSNewLogo: null,
    rankingText: null,
    programDates: "January, September",
    programDuration: "2 Years",
    colorBarHexValue: "#57068C",
  },
  {
    // DegreePageDesign51UCBMastersDataScience - UC Berkeley's Master of Information and Data Science (MIDS) online
    // /masters/online-masters-in-data-science/ucb-master-of-information-and-data-science
    uuid: "58331700-1a02-4abe-a858-4ebe83200d15",
    showUSNewLogo: null,
    rankingText: "",
    programDates: "January, May, September",
    programDuration: "2 Years",
    colorBarHexValue: "#002676",
  },
  {
    // DegreePageDesign51BerkleyCyberSecurityDLNG - UC Berkeley Master's in Cybersecurity online
    // /masters/online-masters-in-cybersecurity/ucb-master-of-information-and-cybersecurity
    // EDUN-11569: 2U requested removing the US News badge while keeping the
    // ranking copy and switching attribution to Fortune magazine.
    uuid: "5c002189-0866-4b10-807e-bcf093346dc0",
    showUSNewLogo: false,
    rankingText: "#2 Online Master's in Cybersecurity",
    rankingDescription: "Ranked by Fortune magazine",
    programDates: "January, May, September",
    programDuration: "2 Years",
    colorBarHexValue: "#002676",
  },
];

export const getDegree51Map: Record<string, DegreePageDesign51> =
  degreePageDesign51.reduce(
    (map, degree) => {
      map[degree.uuid] = degree;
      return map;
    },
    {} as Record<string, DegreePageDesign51>,
  );

export function findCustomFactoidData(
  objectId: string | undefined,
  partners: Array<{ uuid?: string | null }> | undefined,
): DegreePageDesign51 | null {
  // Check if the course objectId matches any course in degreePageDesign51
  let degree51Data = objectId ? getDegree51Map[objectId] : null;

  // If not found by objectId, check if any partners UUID matches
  if (!degree51Data && partners) {
    for (const partner of partners) {
      if (partner.uuid && getDegree51Map[partner.uuid]) {
        degree51Data = getDegree51Map[partner.uuid];
        break;
      }
    }
  }
  return degree51Data;
}

export function processDegree51Data(
  degree51Data: DegreePageDesign51,
  courseDetails: ParsedFactoid[] | undefined,
  labels: Degree51Labels,
): ParsedFactoid[] {
  const extendedCourseDetails: ParsedFactoid[] = [];

  // Add ranking information if available. Decoupled from `showUSNewLogo`
  // so the ranking copy can render even when the badge image is hidden
  // (see EDUN-11569).
  if (degree51Data.rankingText) {
    extendedCourseDetails.push({
      heading: degree51Data.rankingText,
      description: degree51Data.rankingDescription ?? labels.rankedByUsNews,
      sortOrder: 0,
    });
  }

  // Tuition and fees is always null, nothing to add here

  // Add program type information - always 100% Online
  extendedCourseDetails.push({
    heading: labels.online100,
    description: labels.programType,
    sortOrder: extendedCourseDetails.length,
  });

  // Add program dates information - always Flexible dates
  if (degree51Data.programDates) {
    extendedCourseDetails.push({
      heading: labels.flexibleDates,
      description: degree51Data.programDates,
      sortOrder: extendedCourseDetails.length,
    });
  }

  // Add program duration information
  if (degree51Data.programDuration) {
    extendedCourseDetails.push({
      heading: degree51Data.programDuration,
      description: degree51Data.programDuration.toString().includes("Years")
        ? labels.programDuration
        : labels.learnOnYourSchedule,
      sortOrder: extendedCourseDetails.length,
    });
  }

  const courseDetailsLength = courseDetails ? courseDetails.length : 0;
  const hasRoomForAnotherFactoid = extendedCourseDetails.length <= 3;
  if (degree51Data.replacementFactoid && hasRoomForAnotherFactoid) {
    extendedCourseDetails.push(
      buildReplacementFactoid(
        degree51Data.replacementFactoid,
        labels,
        extendedCourseDetails.length,
      ),
    );
  } else if (
    degree51Data.showFreeApplication !== false &&
    hasRoomForAnotherFactoid &&
    courseDetails &&
    courseDetails[courseDetailsLength - 1]
  ) {
    extendedCourseDetails.push({
      heading: labels.freeApplication,
      description: labels.applyForFreeToday,
      sortOrder: courseDetails[courseDetailsLength - 1].sortOrder,
    });
  }

  return extendedCourseDetails;
}
